import type { TovDefinitionEvidenceResponse } from "contracts/api/client-tov/evidence.responses";
import type { TovVersionResponse } from "contracts/api/client-tov/version.responses";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { matchesQuery } from "../../boundaries/text-search.search";

export function hasUnrelatedBrandReference(
  site: OverviewResponse["site"],
  text: string,
): boolean {
  const ownNames = [
    site.displayName,
    site.clientName,
    site.domain,
    site.title,
  ].filter((value): value is string => Boolean(value));
  return site.reviewBrandTerms.some(
    (term) =>
      matchesQuery(term, text) &&
      !ownNames.some((name) => matchesQuery(term, name)),
  );
}

export function guidelineText(version: TovVersionResponse): string[] {
  return [
    ...version.traits.flatMap((item) => [item.label, item.description]),
    ...version.toneDimensions.flatMap((item) => [
      item.label,
      item.lowLabel,
      item.highLabel,
    ]),
    ...version.rules.flatMap((item) => [item.title, item.description]),
    ...version.lexicon.flatMap((item) => [
      item.term,
      item.reason ?? "",
      item.replacement ?? "",
    ]),
    ...version.examples.flatMap((item) => [
      item.inputText ?? "",
      item.outputText,
      item.explanation,
    ]),
    ...version.audiences.flatMap((item) => [
      item.label,
      item.description,
      item.needs,
      item.painPoints,
    ]),
    ...version.contentPillars.flatMap((item) => [
      item.label,
      item.description,
      item.objective,
    ]),
    ...version.journeyStages.flatMap((item) => [item.label, item.description]),
    ...version.channels.flatMap((item) => [item.label, item.description]),
    ...version.visualGuidelines.flatMap((item) => [
      item.title,
      item.description,
    ]),
    ...linkedDefinitions(version).flatMap((item) =>
      item.linkedEvidence.map((link) => link.evidence.claim),
    ),
  ];
}

type LinkedDefinition = {
  linkedEvidence: TovDefinitionEvidenceResponse[];
};

function linkedDefinitions(version: TovVersionResponse): LinkedDefinition[] {
  return [
    ...version.traits,
    ...version.toneDimensions,
    ...version.rules,
    ...version.lexicon,
    ...version.examples,
    ...version.audiences,
    ...version.contentPillars,
    ...version.journeyStages,
    ...version.channels,
    ...version.visualGuidelines,
  ];
}
